import React, { useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { RegionCard } from "./RegionCard";
import VCard from "../../../components/Atomos/Icons/ViewMode/VCard";
import DataTableModal, { type DataTableModalConfig } from "./ModalTable";
import Network from "../../../components/Atomos/Icons/Network";
import type { IDashboardData } from "../../../interfaces/models/IDashboardData";
import { normalizeSpecialEventDashboardEntry } from "../../../utils/specialEventGuestNames";
import CalendarIcon from "../../../assets/images&icons/calendar.svg";
import { PaymentStatus, paymentStatusEnum, statusEnum, StatusType } from "../../../Enums/RegistrationEnum";
import { jurisdictionValueToLabel } from "../../../utils/jurisdiction/structuredJurisdiction";

type ModalConfigBuilder = (
  data: IDashboardData | null,
) => DataTableModalConfig | null;
type ModalConfigBuilderWithType = (
  data: IDashboardData | null,
  type: string,
) => DataTableModalConfig | null;

const hasDashboardRowContent = (
  row: Record<string, unknown>,
  keys: string[],
): boolean =>
  keys.some((key) => String(row[key] ?? "").trim().length > 0);

const MODAL_BUILDERS: Record<string, ModalConfigBuilder> = {
  totalNameBadges: (m) =>
    m?.attendeesByBadgeName
      ? {
          title: "Total Name Badges",
          columns: [
            { header: "Badge Name", accessorKey: "name" },
            { header: "Title", accessorKey: "title" },
            { header: "Firm", accessorKey: "firm" },
            { header: "Jurisdiction", accessorKey: "jurisdiction" },
          ],
          data: m.attendeesByBadgeName
            .map((r: any) => ({
              name: r.name ?? "",
              title: r.listTitle || r.title || "",
              firm: r.firm ?? "",
              jurisdiction: r.jurisdiction ?? "",
            }))
            .filter((row) =>
              hasDashboardRowContent(row, [
                "name",
                "title",
                "firm",
                "jurisdiction",
              ]),
            ),
          showExportToExcel: true,
        }
      : null,

  totalGuests: (m) =>
    m?.attendeesGuests
      ? {
          title: "Total Guests",
          columns: [
            { header: "Guest Name", accessorKey: "guestName" },
            { header: "Name", accessorKey: "name" },
            { header: "Firm", accessorKey: "firm" },
            { header: "Jurisdiction", accessorKey: "jurisdiction" },
          ],
          data: m.attendeesGuests
            .map((r: any) => ({
              guestName: r.guestName ?? "",
              name: r.name ?? "",
              firm: r.firm ?? "",
              jurisdiction: r.jurisdiction ?? "",
            }))
            .filter((row) =>
              hasDashboardRowContent(row, [
                "guestName",
                "name",
                "firm",
                "jurisdiction",
              ]),
            ),
        }
      : null,

  totalPaidRegistrants: (m) =>
    m?.totalPaidRegistrants
      ? {
          title: "Total Paying Attendees",
          columns: [
            { header: "Name", accessorKey: "name" },
            { header: "Firm", accessorKey: "firm" },
            { header: "Jurisdiction", accessorKey: "jurisdiction" },
            { header: "Payment Status", accessorKey: "paymentStatus" },
          ],
          data: m.totalPaidRegistrants
            .map((r: any) => ({
              name: r.name ?? "",
              firm: r.firm ?? "",
              jurisdiction: r.jurisdiction ?? "",
              paymentStatus: statusEnum[r.paymentStatus as StatusType],
            }))
            .filter((row) =>
              hasDashboardRowContent(row, ["name", "firm", "jurisdiction"]),
            ),
          showExportToExcel: true,
        }
      : null,

  jurisdictions: (m) =>
    m?.jurisdictionOffices
      ? {
          title: "Jurisdictions",
          columns: [
            { header: "Jurisdiction", accessorKey: "jurisdiction" },
            { header: "Firm", accessorKey: "firm" },
            { header: "N° Attendees", accessorKey: "attendeesCount" },
          ],
          data: m.jurisdictionOffices.map((r) => ({
            jurisdiction: r.jurisdiction ?? "",
            firm: r.firm ?? "",
            attendeesCount: r.attendeesCount ?? 0,
            attendee: !!r.attendee,
          })),
          inactiveKey: "attendee",
          showExportToExcel: true,
        }
      : null,

  uniqueFirms: (m) =>
    m?.uniqueFirmsList
      ? {
          title: "Unique Firms",
          columns: [
            { header: "Firm", accessorKey: "firm" },
            { header: "Jurisdiction", accessorKey: "jurisdiction" },
            { header: "N° Attendees", accessorKey: "attendeesCount" },
          ],
          data: m.uniqueFirmsList.map((r) => ({
            firm: r.firm ?? "",
            jurisdiction: r.jurisdiction ?? "",
            attendeesCount: r.attendeesCount ?? 0,
            attendee: !!r.attendee,
          })),
          inactiveKey: "attendee",
          showExportToExcel: true,
        }
      : null,
};

const buildLeadershipModalConfig: ModalConfigBuilderWithType = (data, type) => {
  const regionName = type.replace(/^leadership_/, "");
  const region = data?.leadershipAttendees?.find((r) => r.name === regionName);
  if (!region?.firms) {
    return null;
  }
  return {
    title: `Leadership Attendees - ${regionName}`,
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Firm", accessorKey: "firm" },
      { header: "Jurisdiction", accessorKey: "jurisdiction" },
    ],
    data: [...region.firms]
      .sort((a, b) => compareNameLocale(a.name, b.name))
      .map((f) => ({
        name: f.name ?? "",
        firm: f.firm ?? "",
        jurisdiction: f.jurisdiction ?? "",
        attendee: !!f.attendee,
      })),
    inactiveKey: "attendee",
  };
};

const buildRegistrationModalConfig: ModalConfigBuilderWithType = (
  data,
  type,
) => {
  const regionName = type.replace(/^registration_/, "");
  const region = data?.registrationByRegionalRepresentation?.find(
    (r) => r.name === regionName,
  );
  if (!region) {
    return null;
  }

  const officeRows = (region.offices ?? []).map((office) => ({
    name: (office.firmName ?? "").trim(),
    jurisdiction: (office.jurisdiction ?? "").trim(),
    attendee: !!office.attendee,
  }));
  if (officeRows.length === 0) {
    return null;
  }

  return {
    title: `Attendees by Region - ${regionName}`,
    columns: [
      { header: "Name", accessorKey: "name" },
      {
        header: "Jurisdiction",
        accessorKey: "jurisdiction",
        format: (v) => (v ? String(v) : "—"),
      },
      {
        header: "Attending",
        accessorKey: "attendee",
        format: (v) => (v ? "Yes" : "No"),
      },
    ],
    data: officeRows,
    inactiveKey: "attendee",
  };
};

const toDisplayName = (r: any) =>
  (r.badgeName ||
    [r.personName, r.personLastName].filter(Boolean).join(" ").trim() ||
    "") as string;

const compareNameLocale = (a: string | undefined, b: string | undefined) =>
  (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });

/** Display order for Leadership Attendees section (matches server REGION_ID order + Board). */
const LEADERSHIP_ATTENDEE_DISPLAY_ORDER = [
  "Africa Middle East Regional Council",
  "Asia Pacific Regional Council",
  "Europe Regional Council",
  "Latin America Regional Council",
  "North America Regional Council",
  "Board of Directors",
] as const;

function leadershipAttendeeDisplayOrder(name: string): number {
  const idx = (LEADERSHIP_ATTENDEE_DISPLAY_ORDER as readonly string[]).indexOf(
    name,
  );
  return idx === -1 ? LEADERSHIP_ATTENDEE_DISPLAY_ORDER.length : idx;
}

const ATTENDEE_ROLE_MODAL_CONFIG: Record<
  string,
  {
    titlePrefix: string;
    columns: NonNullable<DataTableModalConfig["columns"]>;
    mapRow: (r: any) => Record<string, unknown>;
  }
> = {
  "Guest Speaker": {
    titlePrefix: "Guest Speaker",
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Company", accessorKey: "company" },
    ],
    mapRow: (r) => ({ name: toDisplayName(r), company: r.firmName ?? "" }),
  },
  "Member Speaker": {
    titlePrefix: "Member Speaker",
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Firm", accessorKey: "firm" },
      { header: "Jurisdiction", accessorKey: "jurisdiction" },
    ],
    mapRow: (r) => ({
      name: toDisplayName(r),
      firm: r.firmName ?? "",
      jurisdiction: jurisdictionValueToLabel(r.firmJurisdiction),
    }),
  },
  Sponsor: {
    titlePrefix: "Sponsor",
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Firm", accessorKey: "firm" },
      { header: "Jurisdiction", accessorKey: "jurisdiction" },
    ],
    mapRow: (r) => ({
      name: toDisplayName(r),
      firm: r.firmName ?? "",
      jurisdiction: jurisdictionValueToLabel(r.firmJurisdiction),
    }),
  },
  "Invited Guests": {
    titlePrefix: "Invited Guests",
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Company", accessorKey: "company" },
    ],
    mapRow: (r) => ({ name: toDisplayName(r), company: r.firmName ?? "" }),
  },
  "Paying Member Attendees": {
    titlePrefix: "Paying Member Attendees",
    columns: [
      { header: "Name", accessorKey: "name" },
      { header: "Firm", accessorKey: "firm" },
      { header: "Jurisdiction", accessorKey: "jurisdiction" },
    ],
    mapRow: (r) => ({
      name: toDisplayName(r),
      firm: r.firmName ?? "",
      jurisdiction: jurisdictionValueToLabel(r.firmJurisdiction),
    }),
  },
  "Paying Guests": {
    titlePrefix: "Paying Guests",
    columns: [
      { header: "Guest Name", accessorKey: "guestName" },
      { header: "Name", accessorKey: "name" },
      { header: "Firm", accessorKey: "firm" },
      { header: "Jurisdiction", accessorKey: "jurisdiction" },
    ],
    mapRow: (r) => ({
      guestName: r.guestName ?? "",
      name: r.name ?? "",
      firm: r.firm ?? "",
    }),
  },
  Staff: {
    titlePrefix: "Staff",
    columns: [{ header: "Name", accessorKey: "name" }],
    mapRow: (r) => ({ name: toDisplayName(r) }),
  },
};

const buildAttendeeRoleModalConfig: ModalConfigBuilderWithType = (
  data,
  type,
) => {
  const roleConfig = ATTENDEE_ROLE_MODAL_CONFIG[type];
  const role = data?.attendeeRoles?.find((r) => r.name === type);
  const rawList: unknown[] = Array.isArray(role?.value)
    ? (role?.value ?? [])
    : [];
  if (!roleConfig) {
    return null;
  }
  return {
    title: roleConfig.titlePrefix,
    columns: roleConfig.columns,
    data: rawList.map((r) => roleConfig.mapRow(r)),
  };
};

const SPECIAL_EVENT_ATTENDEE_COLUMNS = [
  { header: "Name", accessorKey: "name" },
  { header: "Firm", accessorKey: "firm" },
  { header: "Jurisdiction", accessorKey: "jurisdiction" },
  { header: "Guests", accessorKey: "guests" },
] as const;

const SPECIAL_EVENT_GUEST_COLUMNS = [
  { header: "Guest Name", accessorKey: "guestName" },
  { header: "Main Attendee Name", accessorKey: "mainAttendeeName" },
  { header: "Firm", accessorKey: "firm" },
  { header: "Jurisdiction", accessorKey: "jurisdiction" },
] as const;

const buildSpecialEventModalConfig: ModalConfigBuilderWithType = (
  data,
  type,
) => {
  const match = type.match(/^specialEvent_(\d+)$/);
  if (!match) {
    return null;
  }
  const idx = parseInt(match[1], 10);
  const specialEvent = data?.specialEvents?.[idx];
  if (!specialEvent) {
    return null;
  }
  const normalizedEvent = normalizeSpecialEventDashboardEntry(specialEvent);
  const attendees = normalizedEvent.attendees ?? [];
  const guestRows = normalizedEvent.guests ?? [];
  const staffAttendees = normalizedEvent.staffAttendees ?? [];
  return {
    title: normalizedEvent.name || "Special Event",
    tabs: [
      {
        label: "Special Event Attendees",
        columns: [...SPECIAL_EVENT_ATTENDEE_COLUMNS],
        data: attendees.map((a) => ({
          name: a.name ?? "",
          firm: a.firm ?? "",
          jurisdiction: a.jurisdiction ?? "",
          guests: typeof a.guests === "number" ? a.guests : 0,
        })),
        showExportToExcel: true,
      },
      {
        label: "Special Event Guests",
        columns: [...SPECIAL_EVENT_GUEST_COLUMNS],
        data: guestRows.map((g) => ({
          guestName: g.guestName ?? "",
          mainAttendeeName: g.mainAttendeeName ?? "",
          firm: g.firm ?? "",
          jurisdiction: g.jurisdiction ?? "",
        })),
        showExportToExcel: true,
      },
      {
        label: "WSG Staff Attendees",
        columns: [...SPECIAL_EVENT_ATTENDEE_COLUMNS],
        data: staffAttendees.map((a) => ({
          name: a.name ?? "",
          firm: a.firm ?? "",
          jurisdiction: a.jurisdiction ?? "",
          guests: typeof a.guests === "number" ? a.guests : 0,
        })),
        showExportToExcel: true,
      },
    ],
    showExportToExcel: true,
  };
};

function getModalConfig(
  type: string,
  data: IDashboardData | null,
): DataTableModalConfig | null {
  const staticBuilder = MODAL_BUILDERS[type];
  if (staticBuilder) {
    return staticBuilder(data);
  }
  if (type.startsWith("leadership_")) {
    return buildLeadershipModalConfig(data, type);
  }
  if (type.startsWith("registration_")) {
    return buildRegistrationModalConfig(data, type);
  }
  if (type.startsWith("specialEvent_")) {
    return buildSpecialEventModalConfig(data, type);
  }
  if (ATTENDEE_ROLE_MODAL_CONFIG[type]) {
    return buildAttendeeRoleModalConfig(data, type);
  }
  return null;
}

interface EventAttendeesDashboardProps {
  data: IDashboardData | null;
}

type MetricIconProps = { className?: string };

const JurisdictionIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className}
    fill="currentColor"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth={2.5}
    viewBox="0 0 128 128"
    aria-hidden="true"
  >
    <path d="M71.11379,106.16462c.083.003.1656.01353.2487.01353a6.73431,6.73431,0,0,0,4.01076-1.30512A58.69275,58.69275,0,0,0,81.96554,99.041l-3.35514-2.34916a52.801,52.801,0,0,1-5.63081,4.93762,2.83071,2.83071,0,0,1-3.85427-.52854,1.99711,1.99711,0,0,0-3.1597.02024A12.14357,12.14357,0,0,1,71.11379,106.16462Z" />
    <path d="M80.81423,115.57228a12.01106,12.01106,0,0,1,.80572,3.9829,61.39142,61.39142,0,0,0,16.819-8.98031l-3.49237-2.44519A57.334,57.334,0,0,1,80.81423,115.57228Z" />
    <path d="M21.2376,102.419c-2.83472-4.3758,1.224-13.36976,3.06871-16.522C27.019,81.26093,23.883,74.5293,19.572,72.01821a5.84031,5.84031,0,0,1-3.25979-5.38287c.29232-9.34055-4.21646-14.65354-5.054-15.55807a11.20341,11.20341,0,0,0-5.50157-3.561A57.68483,57.68483,0,0,1,36.39187,9.81c.0187.06059.03008.12192.05487.18166a6.13883,6.13883,0,0,1,.33169,4.30561C36.37687,15.37352,34.85917,16,33.33853,16.5497a38.23678,38.23678,0,0,0-6.97823,3.23622,4.90689,4.90689,0,0,0-1.70175,6.38187c1.17558,2.45446,4.04263,3.274,6.57568,3.19145,3.41333-.11122,6.47233-1.77215,8.75674-3.20669.127-.07972.35432-.24508.65452-.471,2.40547-1.8061,4.13773-2.68406,5.15936-2.64813A2.04182,2.04182,0,0,0,47.917,21.11811a2.01623,2.01623,0,0,0-1.91532-2.11122c-2.63775-.12992-5.40837,1.67224-7.77644,3.45079-.17323.13041-.3061.23228-.37893.2815-1.8543,1.16437-4.29619,2.51083-6.744,2.59055-.55019.01772-2.371.00492-2.808-.905-.19291-.40207-.17519-.99016.15649-1.19193A33.91032,33.91032,0,0,1,34.70859,20.341c1.78048-.64321,4.76075-1.72047,5.84734-4.63583a10.07633,10.07633,0,0,0-.3868-7.26181,2.00274,2.00274,0,0,0-.13189-.24459,57.24116,57.24116,0,0,1,36.5347-2.14149c-1.74087,2.16788-4.24784,6.00308-3.614,9.52141a2.16771,2.16771,0,0,0,.06594.26181,9.32077,9.32077,0,0,1,.36318,4.00591.82948.82948,0,0,1-.874.594c-2.69582-.17717-6.52647-.13041-9.46834,1.09547a6.75727,6.75727,0,0,0-3.97237,7.15256,12.0122,12.0122,0,0,1-.27952,4.46358,2.01543,2.01543,0,0,0-.29332,2.67024,2.06073,2.06073,0,0,0,2.812.46952c2.5413-1.814,1.7667-8.05709,1.75883-8.12057a2.72268,2.72268,0,0,1,1.52556-2.91388,18.46942,18.46942,0,0,1,7.65341-.79429,4.88326,4.88326,0,0,0,5.07865-3.76526,13.07213,13.07213,0,0,0-.4183-5.9375c-.29865-2.22164,2.35054-5.79823,3.85377-7.38921a57.71288,57.71288,0,0,1,35.59106,37.09369c-4.08458,3.16984-6.56079,10.17821-6.93752,11.30391-.06272.15155-.29128.14795-.42492.12565a.93112.93112,0,0,1-.47466-.26052,48.24466,48.24466,0,0,1-3.312-3.9498c-.02362-.031-.04823-.061-.07382-.091-3.40152-3.9252-8.65733-2.561-8.87682-2.502a4.06644,4.06644,0,0,0-3.16333,2.73573c-.69585,2.36417,1.25687,4.67913,1.626,5.08612.19193.22146-2.01965,3.20226-2.14563,3.32382a7.52963,7.52963,0,0,1-.91435.79281,4.53247,4.53247,0,0,0-5.70168.09547,4.70269,4.70269,0,0,0-.65452,5.89272c.21358.38583,1.564,3.03642,2.185,3.92028a6.9859,6.9859,0,0,0,3.2121,2.4658,3.28133,3.28133,0,0,1,.92846.4246c.23085.215.07544.94-.15638,1.13126l-.36183.3005,3.80967,1.79054a5.74149,5.74149,0,0,0,.744-4.0374,4.17914,4.17914,0,0,0-2.12595-2.70669c-.41043-.22736-.80117-.4065-1.17911-.57677a3.11269,3.11269,0,0,1-1.57182-1.10827c-.45767-.65157-1.72143-3.13091-1.95666-3.55561-.309-.56-.23031-.89518-.18012-.94.0187-.02018.30216-.07283.80806.283,2.65842,1.86959,5.02354-.4124,5.91329-1.27067l.30216-.28789c3.51175-3.24262,4.21548-6.12894,2.063-8.61122-.26673-.2938-.8292-1.16858-.44684-1.25148.1261-.02734,3.02652-.74163,4.76173,1.2062,3.71152,4.81837,4.45464,5.04134,4.997,5.20423,1.94178.58325,4.19336.66637,5.53892-1.115a4.17613,4.17613,0,0,0,.63911-1.18767,28.01023,28.01023,0,0,1,4.30382-8.171,57.20874,57.20874,0,0,1-4.01507,36.86633l3.64782,1.71451A61.47972,61.47972,0,1,0,25.118,111.01476a12.10985,12.10985,0,0,1,3.68941-2.3197A57.95636,57.95636,0,0,1,21.2376,102.419ZM4.91812,51.49772A6.777,6.777,0,0,1,8.0673,53.54331a1.91631,1.91631,0,0,0,.21456.25344c.04232.04281,4.26174,4.36171,4.00092,12.7126a9.5471,9.5471,0,0,0,2.86708,7.26575,11.14219,11.14219,0,0,0,2.4724,1.77018,7.20832,7.20832,0,0,1,3.0964,8.502c-.799,1.395-3.89382,7.13927-4.41276,12.86719A57.15924,57.15924,0,0,1,4.91812,51.49772Z" />
    <path d="M69.54265,111.874H68.53479a8.07216,8.07216,0,0,0-8.06286-8.063H42.33051a8.07216,8.07216,0,0,0-8.06286,8.063H33.25979a8.07216,8.07216,0,0,0-8.06286,8.063v5.03937A3.0269,3.0269,0,0,0,28.22051,128H74.58193a3.0269,3.0269,0,0,0,3.02357-3.02362V119.937A8.07216,8.07216,0,0,0,69.54265,111.874Zm-27.21214-4.0315H60.47193a4.03577,4.03577,0,0,1,4.03143,4.0315H38.29908A4.03577,4.03577,0,0,1,42.33051,107.84252Zm31.24357,16.126H29.22836V119.937a4.03577,4.03577,0,0,1,4.03143-4.0315H69.54265a4.03577,4.03577,0,0,1,4.03143,4.0315Z" />
    <circle cx="55.74625" cy="69.18006" r="2" />
    <circle cx="116.18175" cy="103.94519" r="2" />
    <path d="M121.5211,94.00886,69.11869,69.37906s3.08475-5.32756,3.08743-5.33218a15.54018,15.54018,0,0,0,1.96455.94379A4.10722,4.10722,0,0,0,78.93324,63.064l2.7785-5.07923a4.15066,4.15066,0,0,0-1.5984-5.60039L57.69739,39.83219A4.15892,4.15892,0,0,0,52.102,41.44882l-2.77948,5.07972a4.15129,4.15129,0,0,0,1.59938,5.5999l1.72321.96494L40.11253,74.73776l-2.00046-1.12014a4.15463,4.15463,0,0,0-5.59341,1.61565L29.7382,80.313a4.15124,4.15124,0,0,0,1.59938,5.60039l22.416,12.55216A4.1645,4.1645,0,0,0,59.347,96.84941l2.78145-5.08022a4.15263,4.15263,0,0,0-1.60037-5.5999l-.85432-.47835,2.95781-5.10827,47.7904,33.45968c5.53711,3.87664,13.55206,1.32983,16.41306-4.58124l.02264-.0497A11.56473,11.56473,0,0,0,121.5211,94.00886ZM52.85986,48.46358l2.86708-5.11368,22.416,12.55217a.11358.11358,0,0,1,.03346.14665L75.3083,61.16289l-22.416-12.55217A.11067.11067,0,0,1,52.85986,48.46358ZM58.592,89.83366,55.724,94.94783,33.308,82.39567a.11172.11172,0,0,1-.03445-.14665l2.86806-5.11417L58.55759,89.687A.11172.11172,0,0,1,58.592,89.83366ZM43.63031,76.70749l12.53307-21.644,4.8532,2.71764,7.61958,4.3862L56.15545,83.7209Zm79.57876,30.98985-.02066.0433a7.35479,7.35479,0,0,1-10.50081,2.96753L64.6593,77.08059l2.4318-4.1998,52.70267,24.77079A7.5226,7.5226,0,0,1,123.20907,107.69734Z" />
  </svg>
);

const BuildingIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const NameBadgeIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <rect x="5" y="4" width="14" height="16" rx="2" strokeWidth={2} />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 4V3h6v1M9 14h6M9 17h4"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14.5 9.5a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z"
    />
  </svg>
);

const PeopleIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
    />
  </svg>
);

const PaidRegistrantIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 7h16v10a2 2 0 01-2 2H6a2 2 0 01-2-2V7zM4 7l2-3h12l2 3"
    />
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 12l2.5 2.5L16 9"
    />
  </svg>
);

const UnpaidRegistrantIcon = ({
  className = "w-5 h-5 text-blue-600 flex-shrink-0",
}: MetricIconProps) => (
  <svg
    className={className.replace(/text-\S+/, "text-red-600")}
    fill="currentColor"
    viewBox="0 0 100 100"
    aria-hidden="true"
  >
    <path d="M14.6,78.5V8H49v17.2c0,1.5,1.2,2.7,2.7,2.7h17.2v26.9c0.9-0.1,1.9-0.2,2.9-0.2c0.9,0,1.7,0,2.6,0.1V23.4c0-0.7-0.3-1.4-0.8-1.9L55.4,3.3c-0.5-0.5-1.2-0.8-1.9-0.8H11.9c-1.5,0-2.7,1.2-2.7,2.7v76c0,1.5,1.2,2.7,2.7,2.7h36.7c-0.4-1.7-0.6-3.6-0.6-5.5H14.6z" />
    <path d="M23.3,67.6c0,1.5,1.2,2.6,2.6,2.6h23.4c0.7-1.9,1.6-3.6,2.8-5.3H25.9C24.5,65,23.3,66.2,23.3,67.6z" />
    <path d="M25.9,58.6h31.7c1.5,0,2.6-1.2,2.6-2.6c0-1.5-1.2-2.6-2.6-2.6H25.9c-1.5,0-2.6,1.2-2.6,2.6C23.3,57.4,24.5,58.6,25.9,58.6z" />
    <path d="M34.4,38.8h-3.6c-1.3,0-2.4-0.9-2.7-2.2c-0.1-0.3-0.4-0.6-0.7-0.6H24c-0.4,0-0.8,0.4-0.7,0.8c0.4,3.6,3.3,6.4,6.9,6.7v3.1c0,0.4,0.3,0.7,0.7,0.7h3.4c0.4,0,0.7-0.3,0.7-0.7v-3.1c4.2-0.3,7.4-4.1,6.9-8.4c-0.5-3.8-3.9-6.6-7.8-6.6h-3.3c-1.6,0-2.9-1.4-2.7-3c0.1-1.4,1.4-2.4,2.9-2.4h3.4c1.3,0,2.4,0.9,2.7,2.2c0.1,0.3,0.4,0.6,0.7,0.6h3.4c0.4,0,0.8-0.4,0.7-0.8c-0.4-3.6-3.3-6.4-6.9-6.7v-3.1c0-0.4-0.3-0.7-0.7-0.7h-3.4c-0.4,0-0.7,0.3-0.7,0.7v3.1c-4.2,0.3-7.4,4.1-6.9,8.4c0.5,3.8,3.9,6.6,7.8,6.6h3.1c1.4,0,2.7,1,2.9,2.4C37.3,37.5,36,38.8,34.4,38.8z" />
    <path d="M71.8,59.4c-10.5,0-19,8.5-19,19c0,10.5,8.5,19,19,19s19-8.5,19-19C90.8,67.9,82.3,59.4,71.8,59.4z M80.3,82.7c1.2,1.2,1.2,3.1,0,4.3c-0.6,0.6-1.4,0.9-2.2,0.9c-0.8,0-1.6-0.3-2.1-0.9l-4.2-4.2L67.6,87c-0.6,0.6-1.4,0.9-2.1,0.9c-0.8,0-1.6-0.3-2.2-0.9c-1.2-1.2-1.2-3.1,0-4.3l4.2-4.2l-4.2-4.2c-1.2-1.2-1.2-3.1,0-4.3c1.2-1.2,3.1-1.2,4.3,0l4.2,4.2l4.2-4.2c1.2-1.2,3.1-1.2,4.3,0c1.2,1.2,1.2,3.1,0,4.3l-4.2,4.2L80.3,82.7z" />
  </svg>
);

const SECTION_TITLE_CLASS =
  "text-lg leading-none font-medium text-gray-font font-decimal-medium mb-4";
const FEE_TYPE_LABEL_CLASS =
  "font-sans text-sm font-normal leading-5 tracking-normal text-gray-font";
const SPECIAL_EVENT_COUNT_BADGE_CLASS =
  "inline-flex cursor-pointer rounded py-1 px-4 text-base font-medium transition-opacity hover:opacity-80";

interface MetricCardProps {
  title: string;
  value: React.ReactNode;
  icon: React.ReactNode;
  iconBgClass?: string;
  onClick?: () => void;
  variant?: "default" | "highlight";
}

function MetricCard({
  title,
  value,
  icon,
  iconBgClass = "bg-accent-Default-light",
  onClick,
  variant = "default",
}: MetricCardProps) {
  const isClickable = !!onClick;
  const baseClass =
    "bg-white min-h-[72px] px-3 py-2 md:min-h-[78px] md:px-4 flex items-center gap-3 group";
  const wrapperClass =
    variant === "highlight"
      ? "rounded-lg border-2 border-primary shadow-sm hover:shadow-md transition-shadow duration-200 cursor-pointer"
      : `rounded border border-gray-100 shadow-sm ${isClickable ? "cursor-pointer hover:shadow-md transition-shadow" : "cursor-default"}`;
  const titleClass = isClickable
    ? "group-hover:text-Default transition-colors duration-300"
    : "";

  return (
    <div
      className={`${baseClass} ${wrapperClass} ${isClickable ? "" : "cursor-default"}`}
      onClick={isClickable ? onClick : undefined}
      role={isClickable ? "button" : undefined}
    >
      <div
        className={`flex h-10 w-10 md:h-12 md:w-12 items-center justify-center ${iconBgClass} rounded-full flex-shrink-0`}
      >
        {icon}
      </div>
      <div className="min-w-0">
        <div className="text-2xl md:text-[28px] font-semibold text-gray-font leading-none">
          {value}
        </div>
        <div
          className={`mt-1 text-xs md:text-sm font-medium text-gray-font leading-tight font-decimal-medium ${titleClass}`}
        >
          {title}
        </div>
      </div>
    </div>
  );
}

function EventAttendeesDashboard({
  data,
}: EventAttendeesDashboardProps): React.ReactElement {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [modalConfig, setModalConfig] = useState<DataTableModalConfig | null>(
    null,
  );

  const normalizedSpecialEvents = useMemo(
    () => (data?.specialEvents ?? []).map(normalizeSpecialEventDashboardEntry),
    [data?.specialEvents],
  );

  const openModal = useCallback(
    (type: string, initialTabIndex = 0) => {
      if (!data) {
        return;
      }
      const modalData =
        type.startsWith("specialEvent_") && normalizedSpecialEvents.length > 0
          ? { ...data, specialEvents: normalizedSpecialEvents }
          : data;
      const config = getModalConfig(type, modalData);
      if (!config) {
        return;
      }
      setModalConfig({ ...config, initialTabIndex });
    },
    [data, normalizedSpecialEvents],
  );

  const metricCardsConfig = useMemo(
    () => [
      {
        id: "nameBadges",
        title: "Total Name Badges",
        getValue: (m: IDashboardData) => m.attendeesByBadgeName?.length ?? 0,
        Icon: PeopleIcon,
        onClick: () => openModal("totalNameBadges"),
      },
      {
        id: "guests",
        title: "Total Guests",
        getValue: (m: IDashboardData) => m.attendeesGuests?.length ?? 0,
        Icon: NameBadgeIcon,
        onClick: () => openModal("totalGuests"),
      },
      {
        id: "jurisdictions",
        title: "Jurisdictions",
        getValue: (m: IDashboardData) => m.jurisdictionOffices?.filter(j => j.attendeesCount >=1 )?.length ?? 0,
        Icon: JurisdictionIcon,
        onClick: () => openModal("jurisdictions"),
      },
      {
        id: "uniqueFirms",
        title: "Unique Firms",
        getValue: (m: IDashboardData) => m.uniqueFirms ?? 0,
        Icon: BuildingIcon,
        onClick: () => openModal("uniqueFirms"),
      },
      {
        id: "paidRegistrants",
        title: "Total Paying Attendees",
        getValue: (m: IDashboardData) => m.totalPaidRegistrants?.length ?? 0,
        Icon: PaidRegistrantIcon,
        onClick: () => openModal("totalPaidRegistrants"),
      },
      {
        id: "unpaidRegistrants",
        title: "Unpaid / Partial Attendees",
        getValue: (m: IDashboardData) => m.totalUnpaidRegistrants ?? 0,
        Icon: UnpaidRegistrantIcon,
        iconBgClass: "bg-red-50",
        onClick: () =>
          navigate(`/events-staff/${eventId}/unpaids`, {
            state: {
              filters: {
                eventId: { value: eventId, comparisonType: "EQUAL" },
              },
            },
          }),
        variant: "highlight" as const,
      },
    ],
    [openModal, navigate, eventId],
  );

  const sortedLeadershipAttendees = useMemo(
    () =>
      data?.leadershipAttendees
        ? [...data.leadershipAttendees].sort((a, b) => {
            const oa = leadershipAttendeeDisplayOrder(a.name);
            const ob = leadershipAttendeeDisplayOrder(b.name);
            if (oa !== ob) {
              return oa - ob;
            }
            return compareNameLocale(a.name, b.name);
          })
        : [],
    [data],
  );

  const sortedRegistrationByRegionalRepresentation = useMemo(
    () =>
      data?.registrationByRegionalRepresentation
        ? [...data.registrationByRegionalRepresentation].sort((a, b) =>
            compareNameLocale(a.name, b.name),
          )
        : [],
    [data],
  );

  const feeTypeBarSections = useMemo(() => {
    if (!data) {
      return null;
    }
    const regularFee = data.regularFee ?? 0;
    const lateFee = data.lateFee ?? 0;
    const guestFee = data.guestFee ?? 0;
    const guestLateFee = data.guestLateFee ?? 0;
    const totalRegistrants = regularFee + lateFee + guestFee + guestLateFee;
    const feeTypePercentages = {
      regular: totalRegistrants > 0 ? (regularFee / totalRegistrants) * 100 : 0,
      late: totalRegistrants > 0 ? (lateFee / totalRegistrants) * 100 : 0,
      guest: totalRegistrants > 0 ? (guestFee / totalRegistrants) * 100 : 0,
      guestLate:
        totalRegistrants > 0 ? (guestLateFee / totalRegistrants) * 100 : 0,
    };
    return [
      {
        percentage: feeTypePercentages.regular,
        color: "bg-green-600",
        title: `Regular Fee: ${regularFee}`,
        count: regularFee,
        label: "Regular Fee",
      },
      {
        percentage: feeTypePercentages.late,
        color: "bg-orange-500",
        title: `Late Fee: ${lateFee}`,
        count: lateFee,
        label: "Late Fee",
      },
      {
        percentage: feeTypePercentages.guest,
        color: "bg-blue-500",
        title: `Guest Fee: ${guestFee}`,
        count: guestFee,
        label: "Guest Fee",
      },
      {
        percentage: feeTypePercentages.guestLate,
        color: "bg-cyan-500",
        title: `Guest Late Fee: ${guestLateFee}`,
        count: guestLateFee,
        label: "Guest Late Fee",
      },
    ];
  }, [data]);

  if (data == null) {
    return (
      <div className="mt-2 flex flex-col w-full bg-white font-sans gap-4 md:gap-6 p-6">
        <div className="animate-pulse flex flex-col gap-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 md:gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className="h-32 bg-gray-100 rounded border border-gray-200"
              />
            ))}
          </div>
          <div className="h-48 bg-gray-100 rounded border border-gray-200" />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-col w-full bg-white font-sans gap-4 md:gap-6">
      <div className="grid grid-cols-1 gap-6 ">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 md:gap-6">
          {metricCardsConfig.map((card) => {
            const Icon = card.Icon;
            return (
              <MetricCard
                key={card.id}
                title={card.title}
                value={card.getValue(data)}
                icon={<Icon className="w-6 h-6 text-Default flex-shrink-0" />}
                iconBgClass={card.iconBgClass}
                onClick={card.onClick}
                variant={card.variant}
              />
            );
          })}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">
          <div className="flex flex-col gap-6 ">
            <div className="p-4 rounded border border-gray-200">
              <h3 className={SECTION_TITLE_CLASS}>Attendee Roles</h3>

              <div className="flex gap-4 flex-wrap justify-center">
                {data.attendeeRoles?.map((attendeeRole) => (
                  <button
                    key={attendeeRole.name}
                    className="group relative flex min-h-[102px] w-[152px] flex-col items-center justify-center gap-2 overflow-hidden rounded-lg bg-gray-50 px-2 py-6 text-center text-gray-font"
                    onClick={() => openModal(attendeeRole.name)}
                  >
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-gray-300 opacity-20">
                      <div className="w-24 h-auto [&>svg]:w-full [&>svg]:h-full">
                        <VCard />
                      </div>
                    </div>
                    <div className="relative z-10 w-full text-sm md:text-base font-semibold leading-tight group-hover:text-gray-600 transition-colors duration-300">
                      {attendeeRole.name}
                    </div>
                    <div className="relative z-10 w-full text-3xl font-semibold leading-none flex items-center justify-center text-center gap-2">
                      {attendeeRole.value?.length}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-4 rounded border border-gray-200">
              <h3 className={SECTION_TITLE_CLASS}>Leadership Attendees</h3>
              <div className="flex gap-4 flex-wrap justify-center">
                {sortedLeadershipAttendees.map((region) => (
                  <div
                    key={region.name}
                    className="relative text-gray-font p-3 w-full md:w-[48%] lg:w-[30%] lg:h-[100px] rounded text-center bg-accent-Default-light overflow-hidden cursor-pointer hover:shadow-md transition-shadow group"
                    onClick={() => openModal(`leadership_${region.name}`)}
                  >
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-Default opacity-5">
                      <div className="w-28 md:w-32 h-auto">
                        <Network />
                      </div>
                    </div>
                    <div className="relative z-10 flex flex-col items-center justify-center h-full">
                      <div className="text-sm md:text-base font-medium group-hover:text-blue-400 transition-colors duration-300">
                        {region.name}
                      </div>
                      <div className="flex items-center justify-center gap-2">
                        <div className="text-xl font-bold">
                          {region?.firms?.filter((f: any) => f.attendee).length}{" "}
                          / {region?.firms?.length}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* TODO: uncomment this when the email sends are implemented */}
            {/* <div className="p-4 rounded border border-gray-200">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Event Email Sends</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="bg-accent-Default-dark text-white">
                        <th className="p-2 text-left">#</th>
                        <th className="p-2 text-left">Template Name</th>
                        <th className="p-2 text-left">Sent Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {eventEmailSends.map((row, idx) => (
                        <tr key={row.id} className="even:bg-gray-100 border-b border-gray-200">
                          <td className="p-2">{idx + 1}</td>
                          <td className="p-2">{row.templateTitle ?? "—"}</td>
                          <td className="p-2">{formatTimestampDate(row.sentDateTime)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
            </div> */}

            {normalizedSpecialEvents.length > 0 && (
              <div className="p-4 rounded border border-gray-200">
                <h3 className={SECTION_TITLE_CLASS}>Optional Event</h3>
                <div className="overflow-x-auto overflow-y-scroll max-h-[300px] [scrollbar-gutter:stable]">
                  <table className="min-w-full text-sm border-separate border-spacing-y-2">
                    <thead>
                      <tr className="text-black font-semibold">
                        <th className="p-2 text-left">Event Name</th>
                        <th className="p-2 text-center">Regular</th>
                        <th className="p-2 text-center">Guest</th>
                        <th className="p-2 text-center">WSG Staff</th>
                      </tr>
                    </thead>
                    <tbody>
                      {normalizedSpecialEvents.length === 0 ? (
                        <tr>
                          <td
                            colSpan={4}
                            className="p-2 text-gray-500 text-center"
                          >
                            No special events
                          </td>
                        </tr>
                      ) : (
                        normalizedSpecialEvents.map((row, idx) => (
                          <tr
                            key={idx}
                            className="hover:bg-gray-50 transition-colors"
                          >
                            <td className="p-2 border border-r-0 border-gray-200 font-semibold flex gap-2 items-center">
                              <img
                                src={CalendarIcon}
                                alt="Calendar"
                                className="w-6 h-6 bg-[#E4F3FF] p-1"
                              />
                              <span className="text-base font-semibold">
                                {row.name || "—"}
                              </span>
                            </td>
                            <td className="p-2 border-y border-gray-200 text-center">
                              <button
                                type="button"
                                className={`${SPECIAL_EVENT_COUNT_BADGE_CLASS} bg-[#E4F3FF] text-[#0171CC]`}
                                onClick={() => openModal(`specialEvent_${idx}`, 0)}
                                aria-label={`View regular attendees for ${row.name || "special event"}`}
                              >
                                {row.attendeesCount}
                              </button>
                            </td>
                            <td className="p-2 border-y border-gray-200 text-center">
                              <button
                                type="button"
                                className={`${SPECIAL_EVENT_COUNT_BADGE_CLASS} bg-[#FFE3D3] text-[#FE620A]`}
                                onClick={() => openModal(`specialEvent_${idx}`, 1)}
                                aria-label={`View regular guests for ${row.name || "special event"}`}
                              >
                                {row.guestsCount}
                              </button>
                            </td>
                            <td className="p-2 border border-l-0 border-gray-200 text-center">
                              <button
                                type="button"
                                className={`${SPECIAL_EVENT_COUNT_BADGE_CLASS} bg-[#E4F3FF] text-[#0171CC]`}
                                onClick={() => openModal(`specialEvent_${idx}`, 2)}
                                aria-label={`View WSG staff attendees for ${row.name || "special event"}`}
                              >
                                {row.staffAttendeesCount ?? 0}
                              </button>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>

          <div className="flex flex-col gap-6 ">
            <div className="p-4 rounded border border-gray-200">
              <h3 className={SECTION_TITLE_CLASS}>Attendees by Fee Type</h3>
              <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
                <div className="flex-shrink-0">
                  <div className="text-3xl font-bold text-gray-900 text-center">
                    {(data.regularFee ?? 0) + (data.lateFee ?? 0)}
                  </div>
                  <div className={FEE_TYPE_LABEL_CLASS}>Total Paying Attendees</div>
                </div>
                <div className="flex-1 w-full">
                  <div className="w-full bg-gray-200 h-8 relative flex overflow-hidden">
                    {feeTypeBarSections?.map(
                      (section, index) =>
                        section.percentage > 0 && (
                          <div
                            key={index}
                            className={`${section.color} h-8 relative flex items-center justify-center`}
                            style={{ width: `${section.percentage}%` }}
                            title={section.title}
                          >
                            <span className="text-white text-xs font-medium whitespace-nowrap">
                              {section.count}
                            </span>
                          </div>
                        ),
                    )}
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-2 mt-2 w-full">
                    {feeTypeBarSections?.map((section) => (
                      <div
                        key={section.label}
                        className="flex items-center gap-1.5"
                      >
                        <div
                          className={`w-3 h-3 ${section.color} rounded-full`}
                        />
                        <span className={FEE_TYPE_LABEL_CLASS}>
                          {section.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <div className="text-3xl font-bold text-gray-900 text-center">
                    {(data.guestFee ?? 0) + (data.guestLateFee ?? 0)}
                  </div>
                  <div className={FEE_TYPE_LABEL_CLASS}>Total Guest</div>
                </div>
              </div>
              <div className="grid grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-4">
                <h3 className={`${SECTION_TITLE_CLASS} col-span-full w-full`}>
                  Attendees by Regional Representation
                </h3>
                {sortedRegistrationByRegionalRepresentation.map((region) => (
                  <RegionCard
                    key={region.name}
                    region={region.name}
                    offices={region.offices ?? []}
                    onClick={() => openModal(`registration_${region.name}`)}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        {data?.specialRequests && (
          <div className="p-3 rounded border border-gray-200">
            <h3 className={SECTION_TITLE_CLASS}>Special Requests & Notes</h3>
            <div className="overflow-x-auto overflow-y-scroll max-h-[220px] [scrollbar-gutter:stable]">
              <table className="min-w-full text-sm border-separate border-spacing-y-1">
                <thead>
                  <tr className="text-black font-semibold">
                    <th className="p-1.5 text-left">#</th>
                    <th className="p-1.5 text-left">Name</th>
                    <th className="p-1.5 text-left">Special request</th>
                    <th className="p-1.5 text-left">Note</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.specialRequests ?? []).length === 0 ? (
                    <tr>
                      <td
                        colSpan={4}
                        className="p-1.5 text-gray-500 text-center"
                      >
                        No special requests
                      </td>
                    </tr>
                  ) : (
                    data.specialRequests.map((row, idx) => (
                      <tr
                        key={idx}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="p-1.5 text-sm font-medium w-10">
                          {idx + 1}
                        </td>
                        <td className="p-1.5 text-left text-sm font-semibold">
                          {row.name ?? "—"}
                        </td>
                        <td className="p-1.5 text-left text-sm">
                          {row.specialRequests ?? "—"}
                        </td>
                        <td className="p-1.5 text-left text-sm">
                          {row.note ?? "—"}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <DataTableModal
        config={modalConfig}
        onClose={() => setModalConfig(null)}
      />
    </div>
  );
}

export default React.memo(EventAttendeesDashboard);
